import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown } from 'lucide-react';

function fmtINR(n) {
  if (n === null || n === undefined || n === '') return '—';
  const v = Math.round(Number(n));
  return '₹' + v.toLocaleString('en-IN');
}

export default function PriceChangeReportPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [actor, setActor] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (actor) qs.set('actor', actor);
      if (dateFrom) qs.set('date_from', dateFrom);
      if (dateTo) qs.set('date_to', dateTo);
      const { data } = await api.get(`/reports/proposals/price-changes${qs.toString() ? '?' + qs : ''}`);
      setRows(data.entries || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const totalDelta = useMemo(() => {
    let net = 0;
    for (const r of rows) {
      if (r.field === 'total') net += (Number(r.new) || 0) - (Number(r.old) || 0);
    }
    return net;
  }, [rows]);

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="price-change-report">
      <PageHeader title="Price change report" subtitle="Proposals whose total, rate or discount changed after they were generated or sent." />

      <Card className="p-4 bg-white/80">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Actor (name or user id)</Label>
            <Input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="e.g. Jane" data-testid="pc-actor" />
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="pc-from" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="pc-to" />
          </div>
          <div className="flex items-end">
            <Button onClick={load} className="w-full" data-testid="pc-apply">Apply filters</Button>
          </div>
        </div>
      </Card>

      <Card className="p-4 bg-white/80">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-ink-2">
            <b className="text-ink">{total}</b> change{total === 1 ? '' : 's'} after generate/send
          </div>
          <div className="text-sm text-ink-2">
            Net total shift: <b className={totalDelta >= 0 ? 'text-emerald-600' : 'text-rose-600'}>{totalDelta >= 0 ? '+' : ''}{fmtINR(totalDelta)}</b>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink/10 text-left text-ink-2">
                <th className="py-2 pr-2">When</th>
                <th className="py-2 pr-2">Proposal</th>
                <th className="py-2 pr-2">Client</th>
                <th className="py-2 pr-2">Field</th>
                <th className="py-2 pr-2 text-right">Old</th>
                <th className="py-2 pr-2 text-right">New</th>
                <th className="py-2 pr-2">By</th>
                <th className="py-2 pr-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="py-6 text-center text-ink-2">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-ink-2">No price changes recorded yet.</td></tr>}
              {!loading && rows.map((r, i) => {
                const isMoney = ['total', 'subtotal', 'line.rate', 'line.amount'].includes(r.field);
                const delta = isMoney && r.old != null && r.new != null ? Number(r.new) - Number(r.old) : null;
                return (
                  <tr key={i} className="border-b border-ink/5" data-testid={`pc-row-${i}`}>
                    <td className="py-2 pr-2 whitespace-nowrap text-ink-2">{(r.at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="py-2 pr-2 font-medium"><Link to={`/proposals/${r.proposal_id}`} className="text-blue-700 hover:underline">{r.number}</Link></td>
                    <td className="py-2 pr-2">{r.client_name || '—'}</td>
                    <td className="py-2 pr-2">{r.label || r.field}</td>
                    <td className="py-2 pr-2 text-right">{isMoney ? fmtINR(r.old) : (r.old ?? '—')}</td>
                    <td className="py-2 pr-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isMoney ? fmtINR(r.new) : (r.new ?? '—')}
                        {delta !== null && delta !== 0 && (
                          delta > 0
                            ? <TrendingUp className="h-3 w-3 text-rose-600" />
                            : <TrendingDown className="h-3 w-3 text-emerald-600" />
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-2 text-ink-2">{r.actor_name || '—'}</td>
                    <td className="py-2 pr-2"><Badge variant="outline" className="text-[10px]">{r.status}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
